import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Activity, ChevronLeft, Loader2, Shield, Server, Database,
  Wifi, Clock, Zap, Users, AlertTriangle, CheckCircle, RefreshCw, Home
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { useAuth } from "../../contexts/AuthContext";
import adminApi from "../../lib/adminApi";

const ADMIN_EMAIL = "cpt.serdarakdemir@gmail.com";

export default function AdminSystemStatus() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [status, setStatus] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    fetchStatus();
    
    // Auto refresh every 10 seconds
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchStatus(true);
      }, 10000);
    }
    
    return () => clearInterval(interval);
  }, [autoRefresh]);

  const fetchStatus = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    
    try {
      const response = await adminApi.getSystemStatus();
      setStatus(response.data);
    } catch (error) {
      console.error("Status error:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Check admin access
  if (user?.email !== ADMIN_EMAIL) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Yetkisiz Erişim</h1>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#4DFFFF]" />
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "healthy":
      case "excellent":
        return "text-green-400";
      case "good":
        return "text-[#4DFFFF]";
      case "degraded":
      case "slow":
        return "text-yellow-400";
      default:
        return "text-red-400";
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case "healthy":
      case "excellent":
        return "bg-green-400/20";
      case "good":
        return "bg-[#4DFFFF]/20";
      case "degraded":
      case "slow":
        return "bg-yellow-400/20";
      default:
        return "bg-red-400/20";
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-system-status">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to="/dashboard" className="p-2 rounded-lg bg-[#4DFFFF]/20 hover:bg-[#4DFFFF]/30 text-[#4DFFFF]" title="Ana Sayfa">
            <Home className="w-5 h-5" />
          </Link>
          <Link to="/admin" className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white" title="Admin Panel">
            <ChevronLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-3">
            <Activity className="w-6 h-6 text-[#22C55E]" />
            <h1 className="text-2xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
              Sistem Durumu
            </h1>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {refreshing && <Loader2 className="w-4 h-4 animate-spin text-[#4DFFFF]" />}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={autoRefresh ? "text-green-400" : "text-[#64748B]"}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} style={{ animationDuration: '3s' }} />
            {autoRefresh ? "Otomatik" : "Manuel"}
          </Button>
          <Button onClick={() => fetchStatus()} className="bg-[#4DFFFF] text-[#050E2E]">
            <RefreshCw className="w-4 h-4 mr-2" />
            Yenile
          </Button>
        </div>
      </div>

      {/* Main Status */}
      <div className="glass-card p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-16 h-16 rounded-2xl ${getStatusBg(status?.status)} flex items-center justify-center`}>
              {status?.status === "healthy" ? (
                <CheckCircle className={`w-8 h-8 ${getStatusColor(status?.status)}`} />
              ) : (
                <AlertTriangle className={`w-8 h-8 ${getStatusColor(status?.status)}`} />
              )}
            </div>
            <div>
              <h2 className={`text-2xl font-bold ${getStatusColor(status?.status)}`}>
                {status?.status === "healthy" ? "Sistem Sağlıklı" : "Sistem Sorunlu"}
              </h2>
              <p className="text-[#64748B]">
                Son güncelleme: {new Date(status?.server_time).toLocaleTimeString("tr-TR")}
              </p>
            </div>
          </div>
          
          <div className="text-right">
            <p className="text-sm text-[#64748B]">Uptime</p>
            <p className="text-2xl font-bold text-white">{status?.uptime?.formatted}</p>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Response Time */}
        <div className="glass-card p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className={`p-2 rounded-lg ${getStatusBg(status?.performance?.status)}`}>
              <Zap className={`w-5 h-5 ${getStatusColor(status?.performance?.status)}`} />
            </div>
            <span className="text-sm text-[#64748B]">API Yanıt Süresi</span>
          </div>
          <p className="text-3xl font-bold text-white">{status?.performance?.api_response_time_ms} ms</p>
          <p className={`text-sm ${getStatusColor(status?.performance?.status)}`}>
            {status?.performance?.status === "excellent" ? "Mükemmel" : 
             status?.performance?.status === "good" ? "İyi" : "Yavaş"}
          </p>
        </div>

        {/* Active Users */}
        <div className="glass-card p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-[#4DFFFF]/20">
              <Users className="w-5 h-5 text-[#4DFFFF]" />
            </div>
            <span className="text-sm text-[#64748B]">Çevrimiçi Kullanıcılar</span>
          </div>
          <p className="text-3xl font-bold text-white">{status?.connections?.active_users || 0}</p>
          <p className="text-sm text-[#64748B]">
            {status?.connections?.total_websocket_connections || 0} WebSocket bağlantısı
          </p>
        </div>

        {/* Database Size */}
        <div className="glass-card p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-[#C6A87C]/20">
              <Database className="w-5 h-5 text-[#C6A87C]" />
            </div>
            <span className="text-sm text-[#64748B]">Veritabanı</span>
          </div>
          <p className="text-3xl font-bold text-white">{status?.database?.size_mb} MB</p>
          <p className="text-sm text-[#64748B]">
            {status?.database?.collections} koleksiyon
          </p>
        </div>

        {/* Security */}
        <div className="glass-card p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className={`p-2 rounded-lg ${status?.security?.unauthorized_attempts_last_hour > 0 ? 'bg-red-400/20' : 'bg-green-400/20'}`}>
              <Shield className={`w-5 h-5 ${status?.security?.unauthorized_attempts_last_hour > 0 ? 'text-red-400' : 'text-green-400'}`} />
            </div>
            <span className="text-sm text-[#64748B]">Güvenlik</span>
          </div>
          <p className="text-3xl font-bold text-white">{status?.security?.unauthorized_attempts_last_hour || 0}</p>
          <p className="text-sm text-[#64748B]">Yetkisiz girişim (son 1 saat)</p>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <Clock className="w-5 h-5 text-[#F97316]" />
            <h3 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
              Son {status?.recent_activity?.period} Aktivite
            </h3>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
              <span className="text-[#B1C1C1]">Giriş Yapan</span>
              <span className="text-white font-bold">{status?.recent_activity?.logins || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
              <span className="text-[#B1C1C1]">Yeni Kayıt</span>
              <span className="text-white font-bold">{status?.recent_activity?.registrations || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
              <span className="text-[#B1C1C1]">Yeni Lig</span>
              <span className="text-white font-bold">{status?.recent_activity?.new_leagues || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
              <span className="text-[#B1C1C1]">Yeni Hafta</span>
              <span className="text-white font-bold">{status?.recent_activity?.new_weeks || 0}</span>
            </div>
          </div>
        </div>

        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <Server className="w-5 h-5 text-[#9333EA]" />
            <h3 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
              Sunucu Bilgisi
            </h3>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
              <span className="text-[#B1C1C1]">Başlangıç</span>
              <span className="text-white text-sm">
                {new Date(status?.started_at).toLocaleString("tr-TR")}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
              <span className="text-[#B1C1C1]">Uptime (saniye)</span>
              <span className="text-white font-bold">{status?.uptime?.total_seconds?.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
              <span className="text-[#B1C1C1]">DB Depolama</span>
              <span className="text-white font-bold">{status?.database?.storage_mb} MB</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
              <span className="text-[#B1C1C1]">Sunucu Saati</span>
              <span className="text-white text-sm">
                {new Date(status?.server_time).toLocaleTimeString("tr-TR")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
